import React, { useState, useRef, useEffect, useContext } from 'react';
import { Form, Input, message, Upload } from 'antd';
import './SaveLibraryDesignModal.scss';
import { Icon } from '../../../../components/icon/icon';
import InputComponent from '../../../../components/common/InputComponent/InputComponent';
import ButtonSecondaryLarge from '../../../../components/common/ButtonSecondary/ButtonSecondaryLarge';
import ButtonPrimaryLarge from '../../../../components/common/ButtonPrimary/ButtonPrimaryLarge';
import { Modal } from 'antd';
import { openMessageError } from '../../../../components/common/Message/openMessageError';
import { openMessageSuccess } from '../../../../components/common/Message/openMessageSuccess';
import { DesignerPageContext } from '../../DesignerPage';
import { useTranslation } from 'react-i18next';
import { roundTo } from '../../../../utils/geometry/epsilon';
import {
  convertForeignCurrency,
  convertToUSD,
  convertToVND,
} from '../../../../utils/currency/currency.utils';

const SaveLibraryDesignModal = ({
  open,
  onClose,
  onSave,
  cabinetType,
  editData,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [representativeImage, setRepresentativeImage] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const representativeFileRef = useRef(null);
  const productFilesRef = useRef(null);
  const isKitchen = cabinetType === 'kitchen';
  const { isDesignLibrary, sceneManager } = useContext(DesignerPageContext);
  const num = v =>
    v === undefined || v === null || v === '' ? 0 : parseFloat(v);

  useEffect(() => {
    if (!open) return;

    const result = sceneManager?.getResultEstimatePrice();
    // CREATE mode
    if (!editData) {
      form.resetFields();
      setRepresentativeImage(null);
      setProductImages([]);
      form.setFieldsValue({
        depositPriceVN: roundTo(result?.grand?.vn, 0),
        depositPriceUS: convertToUSD(roundTo(result?.grand?.us, 0)),
      });
      return;
    }
    console.log(roundTo(result?.grand?.us || editData.depositPriceUS, 0));
    // EDIT mode: fill form
    form.setFieldsValue({
      name: editData.name,
      code: editData.code,
      nonRecurringPrice: editData.nonRecurringPrice,
      depositPriceVN: roundTo(result?.grand?.vn || editData.depositPriceVN, 0),
      depositPriceUS: convertToUSD(
        roundTo(result?.grand?.us || editData.depositPriceUS, 0)
      ),
      ...(isKitchen
        ? {
            base: editData.dimensions?.base,
            upper: editData.dimensions?.upper,
            fullHeight: editData.dimensions?.fullHeight,
            countertop: editData.dimensions?.countertop,
            backsplash: editData.dimensions?.backsplash,
          }
        : {
            length: editData.dimensions?.length,
            depth: editData.dimensions?.depth,
            height: editData.dimensions?.height,
          }),
    });

    // EDIT mode: fill ảnh (preview từ url)
    setRepresentativeImage(
      editData.representativeImageUrl
        ? { file: null, preview: editData.representativeImageUrl, isOld: true }
        : null
    );

    setProductImages(
      Array.isArray(editData.productImageUrls)
        ? editData.productImageUrls.map(url => ({
            file: null,
            preview: url,
            isOld: true,
          }))
        : []
    );

    // clear input file để nếu user chọn lại vẫn trigger onChange
    if (representativeFileRef.current) representativeFileRef.current.value = '';
    if (productFilesRef.current) productFilesRef.current.value = '';
  }, [open, editData, isKitchen, form]);

  // Xử lý khi chọn ảnh đại diện
  const handleRepresentativeImageChange = e => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        openMessageError({ content: t('Please select an image file!') });
        return;
      }

      // Validate file size (max 5MB)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        openMessageError({
          content: t('Image must be smaller than {{x}}MB!', { x: 5 }),
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = e => {
        setRepresentativeImage({
          file: file,
          preview: e.target.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Xử lý khi chọn ảnh sản phẩm
  const handleProductImagesChange = e => {
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    // Validate total images (max 10)
    if (productImages.length + files.length > 10) {
      openMessageError({
        content: t('You can upload a maximum of 10 images!'),
      });
      return;
    }

    const validFiles = [];

    files.forEach(file => {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        openMessageError({
          content: t('{{x}} is not an image file!', { x: file.name }),
        });
        return;
      }

      // Validate file size (max 5MB)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        openMessageError({
          content: t('{{y}} must be smaller than {{x}}MB!', {
            x: 5,
            y: file.name,
          }),
        });
        return;
      }

      validFiles.push(file);
    });

    // Read and add valid files
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = e => {
        setProductImages(prev => [
          ...prev,
          {
            file: file,
            preview: e.target.result,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Xóa ảnh đại diện
  const handleRemoveRepresentativeImage = () => {
    setRepresentativeImage(null);
    if (representativeFileRef.current) {
      representativeFileRef.current.value = '';
    }
  };

  // Xóa ảnh sản phẩm
  const handleRemoveProductImage = index => {
    setProductImages(prev => prev.filter((_, i) => i !== index));
  };

  // Xử lý submit form
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Validate ảnh đại diện
      if (!representativeImage) {
        openMessageError({ content: t('Please upload a thumbnail') });
        setLoading(false);
        return;
      }

      const dimensions = isKitchen
        ? {
            base: num(values.base), // Bếp dưới
            upper: num(values.upper), // Bếp trên
            fullHeight: num(values.fullHeight), // Bếp đụng trần
            countertop: num(values.countertop), // Mặt bếp
            backsplash: num(values.backsplash), // Vách bếp
          }
        : {
            length: num(values.length), // Dài
            depth: num(values.depth), // Sâu
            height: num(values.height), // Cao
          };

      // Chuẩn bị dữ liệu để gửi
      const formData = {
        name: values.name,
        code: values.code,
        dimensions,
        representativeImage: representativeImage.file,
        productImages: productImages.map(img => img.file),
        nonRecurringPrice: parseFloat(values.nonRecurringPrice) || 0,
        depositPriceVN: parseFloat(values.depositPriceVN) || 0,
        depositPriceUS: convertToVND(parseFloat(values.depositPriceUS)) || 0,
      };

      // Gọi hàm onSave từ parent component
      await onSave(formData);

      // Reset form sau khi lưu thành công
      handleCancel();
      // openMessageSuccess({ content: t('Library design saved successfully!') });
    } catch (error) {
      console.error('Validation failed:', error);
      openMessageError({ content: t('Please fill in all required fields!') });
    } finally {
      setLoading(false);
    }
  };

  // Đóng modal và reset form
  const handleCancel = () => {
    form.resetFields();
    setRepresentativeImage(null);
    setProductImages([]);
    if (representativeFileRef.current) {
      representativeFileRef.current.value = '';
    }
    if (productFilesRef.current) {
      productFilesRef.current.value = '';
    }
    onClose();
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-8">
          <span className="text-headline-small">
            {isDesignLibrary ? t('Update design') : t('Save design')}
          </span>
        </div>
      }
      classNames={{
        header: '!p-24 !pb-16 !m-0',
        content: '!p-0',
        body: '!p-0 !m-0',
      }}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={1024}
      className="save-library-design-modal"
      centered
      destroyOnHidden
    >
      <div className="modal-content-wrapper">
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ flex: '130px' }}
          labelWrap
          labelAlign={'right'}
          requiredMark={(labelNode, { required }) => (
            <div className="flex items-center justify-end self-stretch">
              {labelNode}
              {required ? (
                <span style={{ color: 'red', fontSize: '12px' }}>* </span>
              ) : null}
            </div>
          )}
          colon={false}
          className="flex px-24 py-16 flex-col items-start gap-16 grow self-stretch"
          autoComplete="off"
        >
          {/* Tên sản phẩm */}
          <Form.Item
            label={
              <span className="text-body-medium" style={{ fontWeight: 600 }}>
                {t('Product name')}
              </span>
            }
            name="name"
            className="self-stretch"
            rules={[
              { required: true, message: t('Please enter the product name!') },
            ]}
          >
            <InputComponent placeholder="Nhập tên sản phẩm" />
          </Form.Item>

          {/* Mô tả sản phẩm */}
          <Form.Item
            label={
              <span className="text-body-medium" style={{ fontWeight: 600 }}>
                {t('Description')}
              </span>
            }
            name="code"
            className="self-stretch"
            rules={[
              {
                required: true,
                message: t('Please enter the product description!'),
              },
            ]}
          >
            <InputComponent placeholder={t('Enter description')} />
          </Form.Item>

          {/* Kích thước */}
          <Form.Item
            label={
              <span className="text-body-medium" style={{ fontWeight: 600 }}>
                {t('Size')}
              </span>
            }
            required={false}
          >
            {isKitchen ? (
              <div className="flex gap-24 items-center align-sefl-stretch">
                <Form.Item name="base">
                  <InputComponent
                    placeholder={t('Base Cabinet')}
                    addonAfter={<span className="text-label-small">mm</span>}
                  />
                </Form.Item>

                <Form.Item name="upper">
                  <InputComponent
                    placeholder={t('Upper Cabinet')}
                    addonAfter={<span className="text-label-small">mm</span>}
                  />
                </Form.Item>

                <Form.Item name="fullHeight">
                  <InputComponent
                    placeholder={t('Ceiling Cabinet')}
                    addonAfter={<span className="text-label-small">mm</span>}
                  />
                </Form.Item>

                <Form.Item name="countertop">
                  <InputComponent
                    placeholder={t('Countertop')}
                    addonAfter={<span className="text-label-small">mm</span>}
                  />
                </Form.Item>

                <Form.Item name="backsplash">
                  <InputComponent
                    placeholder={t('Backsplash')}
                    addonAfter={<span className="text-label-small">mm</span>}
                  />
                </Form.Item>
              </div>
            ) : (
              <div className="flex gap-24 items-center align-sefl-stretch">
                <Form.Item name="length">
                  <InputComponent
                    placeholder={t('Length')}
                    addonAfter={<span className="text-label-small">mm</span>}
                  />
                </Form.Item>

                <Form.Item name="depth">
                  <InputComponent
                    placeholder={t('Depth')}
                    addonAfter={<span className="text-label-small">mm</span>}
                  />
                </Form.Item>

                <Form.Item name="height">
                  <InputComponent
                    placeholder={t('Height')}
                    addonAfter={<span className="text-label-small">mm</span>}
                  />
                </Form.Item>
              </div>
            )}
          </Form.Item>

          {/* Ảnh đại diện */}
          <Form.Item
            label={
              <span className="text-body-medium" style={{ fontWeight: 600 }}>
                {t('Thumbnail')}
              </span>
            }
            required
          >
            <div className="image-upload-section">
              {!representativeImage ? (
                <div
                  className="upload-placeholder"
                  onClick={() => representativeFileRef.current?.click()}
                >
                  <input
                    ref={representativeFileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleRepresentativeImageChange}
                  />
                  <div className="upload-icon">
                    <Icon.ICAddImage />
                  </div>
                </div>
              ) : (
                <div className="image-preview-wrapper">
                  <img
                    src={representativeImage.preview}
                    alt="Representative"
                    className="image-preview"
                  />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={handleRemoveRepresentativeImage}
                  >
                    <Icon.ICDelete />
                  </button>
                </div>
              )}
            </div>
          </Form.Item>

          {/* Ảnh sản phẩm khác */}
          <Form.Item
            label={
              <span className="text-body-medium" style={{ fontWeight: 600 }}>
                {t('Additional Product Images')}
              </span>
            }
          >
            <div className="multiple-images-section">
              {/* Upload button - luôn hiển thị đầu tiên nếu < 10 ảnh */}
              {productImages.length < 10 && (
                <div
                  className="upload-placeholder"
                  onClick={() => productFilesRef.current?.click()}
                >
                  <input
                    ref={productFilesRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleProductImagesChange}
                  />
                  <div className="upload-icon">
                    <Icon.ICAddImage />
                  </div>
                </div>
              )}

              {/* List ảnh sản phẩm */}
              {productImages.length > 0 && (
                <div className="images-list">
                  {productImages.map((img, index) => (
                    <div key={index} className="image-preview-wrapper">
                      <img
                        src={img.preview}
                        alt={`Product ${index + 1}`}
                        className="image-preview"
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => handleRemoveProductImage(index)}
                      >
                        <Icon.ICDelete />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Form.Item>

          {/* Giá không lặp đặt */}
          <Form.Item
            label={
              <span className="text-body-medium" style={{ fontWeight: 600 }}>
                {t('Price without installation')}
              </span>
            }
            name="nonRecurringPrice"
            className="self-stretch"
            // rules={[
            //   { required: true, message: t('Please enter a price!') },
            //   {
            //     pattern: /^\d+(\.\d+)?$/,
            //     message: t('Only input numbers!'),
            //   },
            // ]}
          >
            <InputComponent
              type="number"
              placeholder={t('Enter price')}
              addonAfter={<span className="text-label-medium">₫</span>}
            />
          </Form.Item>

          {/* Giá có lặp đặt */}
          <Form.Item
            label={
              <span className="text-body-medium" style={{ fontWeight: 600 }}>
                {t('Price with installation')}
              </span>
            }
            name="depositPriceVN"
            className="self-stretch"
            rules={[
              { required: true, message: t('Please enter a price!') },
              {
                pattern: /^\d+(\.\d+)?$/,
                message: t('Only input numbers!'),
              },
            ]}
          >
            <InputComponent
              // type="number"
              placeholder={t('Enter price')}
              addonAfter={<span className="text-label-medium">₫</span>}
            />
          </Form.Item>
          <Form.Item
            label={
              <span className="text-body-medium" style={{ fontWeight: 600 }}>
                {t('Price with installation') + ' US'}
              </span>
            }
            name="depositPriceUS"
            className="self-stretch"
            rules={[
              { required: true, message: t('Please enter a price!') },
              {
                pattern: /^\d+(\.\d+)?$/,
                message: t('Only input numbers!'),
              },
            ]}
          >
            <InputComponent
              // type="number"
              placeholder={t('Enter price')}
              addonAfter={<span className="text-label-medium">$</span>}
            />
          </Form.Item>
        </Form>

        {/* Footer buttons */}
        <div className="modal-footer">
          <ButtonSecondaryLarge title={t('Cancel')} onClick={handleCancel} />
          <ButtonPrimaryLarge
            title={isDesignLibrary ? t('Update design') : t('Save design')}
            onClick={handleSubmit}
            loading={loading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SaveLibraryDesignModal;
